import { useContext, useMemo, useCallback } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, $getRoot, $createParagraphNode } from 'lexical';
import { AllowedKeysContext } from '../components/AllowedKeysContext';
import {
    $createFractionNode,
    $createExponentNode,
    $createRootNode,
    $createOverlineNode,
} from '../components/MathNodes';

import { MathSymbols } from '@/src/lib';

type SymbolBtn = { key: string; label: string; type: 'text' | 'node' };

interface MathKeypadPluginProps {
    disabled?: boolean;
}

export default function MathKeypadPlugin({ disabled }: MathKeypadPluginProps) {
    const [editor] = useLexicalComposerContext();
    const allowedKeys = useContext(AllowedKeysContext);

    const allow = useCallback(
        (k: string) => {
            const keys = allowedKeys ?? [];
            return keys.includes(k);
        },
        [allowedKeys]
    );

    // ALL BUTTONS //
    const numberButtons = useMemo(() => {
        const base = Object.keys(MathSymbols.numbers);
        const out: string[] = [];
        for (const n of base) {
            if (allow(n)) {
                out.push(n);
            }
        }
        return out;
    }, [allow]);

    const letterButtons = useMemo(() => {
        const out: string[] = [];
        for (const l of Object.keys(MathSymbols.letters_lower)) {
            if (allow(l)) {
                out.push(l);
            }
        }

        for (const l of Object.keys(MathSymbols.letters_upper)) {
            if (allow(l)) {
                out.push(l);
            }
        }

        return out;
    }, [allow]);

    const symbolButtons = useMemo<SymbolBtn[]>(() => {
        const out: SymbolBtn[] = [];
        // text symbols
        const symbols = Object.entries(MathSymbols.symbols);
        for (const [key, value] of symbols) {
            if (allow(key)) {
                out.push({ key, label: value, type: 'text' });
            }
        }

        return out;
    }, [allow]);

    const expressionButtons = useMemo<SymbolBtn[]>(() => {
        const out: SymbolBtn[] = [];

        // structural nodes
        if (allow('frac')) out.push({ key: 'frac', label: 'a⁄b', type: 'node' });
        if (allow('sqrt')) out.push({ key: 'sqrt', label: '√', type: 'node' });
        if (allow('nrt')) out.push({ key: 'nrt', label: 'ⁿ√', type: 'node' });
        if (allow('exp')) out.push({ key: 'exp', label: 'xⁿ', type: 'node' });
        if (allow('seg')) out.push({ key: 'seg', label: 'seg', type: 'node' });
        if (allow('line')) out.push({ key: 'line', label: 'line', type: 'node' });
        if (allow('ray')) out.push({ key: 'ray', label: 'ray', type: 'node' });

        return out;
    }, [allow]);

    // Ensure we have a valid RangeSelection at the end of the document.
    const ensureCaretAtEnd = useCallback(() => {
        const root = $getRoot();
        if (root.getChildrenSize() === 0) {
            root.append($createParagraphNode());
        }
        root.selectEnd();
        return $getSelection();
    }, []);

    const insertText = useCallback(
        (text: string) => {
            if (disabled) return;

            // Find the currently focused contenteditable
            const activeEl = document.activeElement;
            if (activeEl && activeEl.getAttribute('contenteditable') === 'true') {
                // Use execCommand which works for all contenteditables including Lexical
                // This maintains focus and inserts at the current cursor position
                document.execCommand('insertText', false, text);
                return;
            }

            // Fallback: no focused contenteditable, use main editor
            editor.update(() => {
                let sel = $getSelection();
                if (!$isRangeSelection(sel)) {
                    sel = ensureCaretAtEnd();
                }
                if ($isRangeSelection(sel)) {
                    sel.insertText(text);
                }
            });
        },
        [disabled, editor, ensureCaretAtEnd]
    );

    const insertNode = useCallback(
        (kind: 'frac' | 'sqrt' | 'nrt' | 'exp' | 'seg' | 'line' | 'ray') => {
            if (disabled) return;

            // Check if a slot editor is currently focused
            const activeEl = document.activeElement;
            const isSlotFocused =
                activeEl?.getAttribute('contenteditable') === 'true' &&
                activeEl.closest('.pm-math-slot') !== null;

            if (isSlotFocused) {
                // Dispatch custom event for the slot to handle
                const slotContainer = activeEl.closest('.pm-math-slot');
                if (slotContainer) {
                    const customEvent = new CustomEvent('math-node-insert', {
                        bubbles: true,
                        detail: { kind },
                    });
                    slotContainer.dispatchEvent(customEvent);
                    return;
                }
            }

            // Insert into main editor
            editor.update(() => {
                let sel = $getSelection();
                if (!$isRangeSelection(sel)) {
                    sel = ensureCaretAtEnd();
                }
                if (!$isRangeSelection(sel)) return;

                let node: any;
                switch (kind) {
                    case 'frac': {
                        node = $createFractionNode();
                        sel.insertNodes([node]);
                        break;
                    }
                    case 'exp': {
                        node = $createExponentNode();
                        sel.insertNodes([node]);
                        break;
                    }
                    case 'sqrt': {
                        // Square root: index = null
                        node = $createRootNode(false);
                        node.__index = null;
                        sel.insertNodes([node]);
                        break;
                    }
                    case 'nrt': {
                        // Nth root: index substate exists
                        node = $createRootNode(true);
                        node.__index = { state: '' };
                        sel.insertNodes([node]);
                        break;
                    }
                    case 'seg':
                        node = $createOverlineNode('segment');
                        sel.insertNodes([node]);
                        break;
                    case 'line':
                        node = $createOverlineNode('line');
                        sel.insertNodes([node]);
                        break;
                    case 'ray': {
                        // Overline variants
                        node = $createOverlineNode('ray');
                        sel.insertNodes([node]);
                        break;
                    }
                }

                // Focus the first slot after insertion
                if (node) {
                    const nodeKey = node.getKey();
                    setTimeout(() => {
                        const dom = editor.getElementByKey(nodeKey);
                        if (dom) {
                            // For nth root, focus index first; for others, focus the first slot
                            const firstSlot = dom.querySelector<HTMLElement>(
                                kind === 'nrt'
                                    ? '.pm-math-index [contenteditable="true"]'
                                    : '.pm-math-num [contenteditable="true"], ' +
                                          '.pm-math-base [contenteditable="true"], ' +
                                          '.pm-math-radicand [contenteditable="true"], ' +
                                          '.pm-math-overline [contenteditable="true"]'
                            );
                            firstSlot?.focus();
                        }
                    }, 0);
                }
            });
        },
        [disabled, editor, ensureCaretAtEnd]
    );

    const onSymbolClick = (key: string, label: string, type: 'text' | 'node') => {
        if (disabled) return;
        if (type === 'text') insertText(key === 'spc' ? ' ' : label);
        else insertNode(key as any);
    };

    const numberButtonsHtml = useMemo(() => {
        if (numberButtons.length === 0) return null;

        return (
            <div className="pm-keypad-group">
                {numberButtons.map((t) => (
                    <button
                        key={`num-${t}`}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertText(t)}
                        className="pm-keypad-btn"
                        disabled={disabled}
                    >
                        {t}
                    </button>
                ))}
            </div>
        );
    }, [numberButtons, insertText, disabled]);

    const letterButtonsHtml = useMemo(() => {
        if (letterButtons.length === 0) return null;

        return (
            <div className="pm-keypad-group">
                {letterButtons.map((t) => (
                    <button
                        key={`letter-${t}`}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertText(t)}
                        className="pm-keypad-btn"
                        disabled={disabled}
                    >
                        {t}
                    </button>
                ))}
            </div>
        );
    }, [letterButtons, insertText, disabled]);

    const symbolButtonsHtml = useMemo(() => {
        if (symbolButtons.length === 0) return null;

        return (
            <div className="pm-keypad-group">
                {symbolButtons.map(({ key, label, type }) => (
                    <button
                        key={`sym-${key}`}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onSymbolClick(key, label, type)}
                        className="pm-keypad-btn"
                        disabled={disabled}
                    >
                        {label}
                    </button>
                ))}
            </div>
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolButtons, disabled]);

    const expressionButtonsHtml = useMemo(() => {
        if (expressionButtons.length === 0) return null;

        return (
            <div className="pm-keypad-group">
                {expressionButtons.map(({ key, label, type }) => {
                    const title = getExpressionTitle(key);
                    return (
                        <button
                            key={`sym-${key}`}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onSymbolClick(key, label, type)}
                            className="pm-keypad-btn"
                            disabled={disabled}
                            title={title}
                        >
                            {renderExpressionLabel(label)}
                        </button>
                    );
                })}
            </div>
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expressionButtons, disabled]);

    return (
        <div className="pm-math-keypad" style={{ display: 'grid', gap: 12 }}>
            {numberButtonsHtml}
            {letterButtonsHtml}
            {symbolButtonsHtml}
            {expressionButtonsHtml}
        </div>
    );
}

function getExpressionTitle(key: string): string {
    if (key === 'frac') return 'Insert Fraction';
    if (key === 'sqrt') return 'Insert Square Root';
    if (key === 'nrt') return 'Insert Nth Root';
    if (key === 'exp') return 'Insert Exponent';
    if (key === 'seg') return 'Insert Line Segment';
    if (key === 'line') return 'Insert Line';
    if (key === 'ray') return 'Insert Ray';
    return '';
}
function renderExpressionLabel(label: string): React.ReactNode {
    // Handle special cases for overline symbols
    if (label === 'seg') {
        return (
            <svg width="40" height="30">
                {/* Changed y from 10 to 7 */}
                <line x1="10" y1="7" x2="30" y2="7" stroke="black" stroke-width="1.5" />
                <text x="20" y="25" text-anchor="middle" font-family="Arial">
                    AB
                </text>
            </svg>
        );
    } else if (label === 'line') {
        return (
            <svg width="40" height="30">
                <defs>
                    <marker
                        id="line-arrow-small"
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerWidth="4"
                        markerHeight="4"
                        orient="auto-start-reverse"
                    >
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="black" />
                    </marker>
                </defs>
                <line
                    x1="10"
                    y1="7"
                    x2="30"
                    y2="7"
                    stroke="black"
                    stroke-width="1.5"
                    marker-start="url(#line-arrow-small)"
                    marker-end="url(#line-arrow-small)"
                />
                <text x="20" y="25" text-anchor="middle" font-family="Arial">
                    AB
                </text>
            </svg>
        );
    } else if (label === 'ray') {
        return (
            <svg width="40" height="30">
                <defs>
                    <marker
                        id="arrow-small"
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerWidth="4"
                        markerHeight="4"
                        orient="auto-start-reverse"
                    >
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="black" />
                    </marker>
                </defs>
                <line
                    x1="10"
                    y1="7"
                    x2="30"
                    y2="7"
                    stroke="black"
                    stroke-width="1.5"
                    marker-end="url(#arrow-small)"
                />
                <text x="20" y="25" text-anchor="middle" font-family="Arial">
                    AB
                </text>
            </svg>
        );
    }
    return label;
}
